import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    CardBody,
    CardHeadingIcon,
    CardLink,
    Description,
    Group,
    Heading,
    type IconDefinition,
} from '@gitbook/spine-react';

import { useHomeContext } from '../../HomeContext';
import { HomeSection, HomeSectionHeading } from '../HomeSection';

export function LearnGitBook() {
    const { organization } = useHomeContext();

    const cards: LearnCard[] = organization.useCase
        ? {
              [api.OrganizationUseCase.InternalDocs]: internalDocsCards,
              [api.OrganizationUseCase.DocsSite]: docsSiteCards,
              [api.OrganizationUseCase.AudienceControlledSite]: docsSiteCards,
          }[organization.useCase]
        : null;

    if (!cards) {
        return null;
    }

    return (
        <HomeSection>
            <HomeSectionHeading>Learn GitBook</HomeSectionHeading>
            <ul className="grid h-full grid-cols-1 gap-6 lg:grid-cols-3">
                {cards.map((card) => (
                    <li key={card.url}>
                        <CardLink href={card.url} className="flex h-full">
                            <CardBody>
                                <Group>
                                    <CardHeadingIcon>{card.icon}</CardHeadingIcon>
                                    <Heading>{card.title}</Heading>
                                </Group>
                                <Description>{card.description}</Description>
                            </CardBody>
                        </CardLink>
                    </li>
                ))}
            </ul>
        </HomeSection>
    );
}

type LearnCard = { title: string; description: string; icon: IconDefinition; url: string };

const internalDocsCards: LearnCard[] = [
    {
        title: 'Editing content',
        description:
            'Discover how to get the most from GitBook’s editing tools, and start adding rich, engaging content to your docs.',
        icon: Icon.Edit,
        url: 'https://gitbook.com/docs/content-editor/editing-content',
    },
    {
        title: 'Organizing content',
        description:
            'Explore how you can use pages, spaces and collections to organize your work, and control permissions on everything.',
        icon: Icon.Organization,
        url: 'https://gitbook.com/docs/content-editor/editor/content-structure',
    },
    {
        title: 'Searching content',
        description:
            'Find the information you need fast with Quick find, or take things further with AI-powered search. You ask, it answers.',
        icon: Icon.SearchOrAsk,
        url: 'https://gitbook.com/docs/content-editor/searching-your-content',
    },
];
const docsSiteCards: LearnCard[] = [
    {
        title: 'Adding content to your site',
        description:
            'Learn how to link one or more GitBook spaces to your docs site to populate it with content, ready for publishing.',
        icon: Icon.DocsSite,
        url: 'https://gitbook.com/docs/published-documentation/publish-your-content-as-a-docs-site#create-a-docs-site',
    },
    {
        title: 'Branding your docs site',
        description:
            'Explore how to customize your site with your own colors, logo, custom domain, and more.',
        icon: Icon.Customization,
        url: 'https://gitbook.com/docs/published-documentation/customization',
    },
    {
        title: 'Changing audience settings',
        description:
            'Discover the publishing options for your site, including how you can publish to everyone, or just specific groups.',
        icon: Icon.PublishStatusVisitorAuth,
        url: 'https://gitbook.com/docs/published-documentation/publish-your-content-as-a-docs-site',
    },
];
